import { Grabber } from '@/components/Grabber';
import { Button } from '@components/Button/Button';
import { FoldableSection } from '@components/FoldableSection/FoldableSection';
import {
  buildEmptyReportFilters,
  countActiveReportFilterFields,
  hasActiveReportFilters,
  resolveReportFilterField,
  sanitizeReportMultiSelectValue,
} from '@components/ReportFilters/reportFilters.utils';
import { Ionicons } from '@expo/vector-icons';
import { useAppTranslation } from '@hooks/useAppTranslation';
import { useEffect, useMemo } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { Modal, Platform } from 'react-native';
import { ScrollView, SizableText, XStack, YStack, useTheme } from 'tamagui';
import { REPORT_FILTERS_PANEL_WIDTH } from '../reportFilters.constants';
import type { ReportFiltersValues } from '../reportFilters.types';
import { renderReportFilterField } from './fields/ReportFilterFieldRenderer';
import type { ReportFiltersPanelProps } from './reportFiltersPanel.types';

export const ReportFiltersPanel = ({
  config,
  defaultValues,
  open,
  variant,
  onApply,
  onClose,
}: ReportFiltersPanelProps): React.ReactElement | null => {
  const { t } = useAppTranslation();
  const theme = useTheme();

  const formMethods = useForm<ReportFiltersValues>({
    mode: 'onChange',
    defaultValues,
  });

  const {
    control,
    register,
    handleSubmit,
    unregister,
    getFieldState,
    setError,
    clearErrors,
    setValue,
    trigger,
    formState,
    resetField,
    reset,
    getValues,
    watch,
    setFocus,
    subscribe,
  } = formMethods;

  const emptyFilters = useMemo(() => buildEmptyReportFilters(), []);
  const watchedValues = watch();
  const hasActiveFilters = useMemo(
    () => hasActiveReportFilters(watchedValues, emptyFilters),
    [emptyFilters, watchedValues],
  );
  const sectionActiveCounts = useMemo(
    () =>
      config.sections.map((section) =>
        countActiveReportFilterFields(section, watchedValues, emptyFilters),
      ),
    [config.sections, emptyFilters, watchedValues],
  );
  const resolvedSections = useMemo(
    () =>
      config.sections.map((section) => ({
        ...section,
        fields: section.fields.map((field) =>
          resolveReportFilterField(field, watchedValues),
        ),
      })),
    [config.sections, watchedValues],
  );

  useEffect(() => {
    if (!open) {
      return;
    }

    reset(defaultValues);
  }, [defaultValues, open, reset]);

  useEffect(() => {
    config.sections.forEach((section) => {
      section.fields.forEach((field) => {
        if (field.type !== 'multi-select') {
          return;
        }

        const sanitizedValues = sanitizeReportMultiSelectValue(
          field,
          watchedValues,
        );

        if (!sanitizedValues) {
          return;
        }

        setValue(field.id, sanitizedValues, { shouldValidate: true });
      });
    });
  }, [config.sections, setValue, watchedValues]);

  const content = (
    <YStack gap="$2" padding="$4" flex={1}>
      <XStack alignItems="center" justifyContent="space-between" gap="$3">
        <XStack alignItems="center" gap="$true" flex={1}>
          <Button
            variant="ghost"
            accessibilityLabel={t('filters.close')}
            icon={<Ionicons name="close" size={20} color={theme.color?.val} />}
            onPress={onClose}
            size="$3"
            padding="$0"
          />
          <SizableText fontFamily="$bold" size="$h6">
            {t('filters.title')}
          </SizableText>
        </XStack>
        <XStack alignItems="center" gap="$1">
          {hasActiveFilters && (
            <Button
              variant="ghostBrand"
              title={t('filters.clear')}
              onPress={() => reset(emptyFilters)}
              padding="$1"
            />
          )}
          <Button
            variant="ghostBrand"
            title={t('filters.apply')}
            onPress={handleSubmit(onApply)}
            padding="$1"
          />
        </XStack>
      </XStack>
      <YStack height="$separator" backgroundColor="$color" />
      <ScrollView showsVerticalScrollIndicator={false} flex={1}>
        <YStack gap="$4">
          {resolvedSections.map((section, index) => {
            const activeCount = sectionActiveCounts[index] ?? 0;

            return (
              <FoldableSection
                key={section.title}
                title={section.title}
                titleSlot={
                  <XStack alignItems="center" gap="$2">
                    <SizableText size="$body" fontFamily="$bold" color="$color">
                      {section.title}
                    </SizableText>
                    {activeCount > 0 && (
                      <SizableText
                        size="$body"
                        fontFamily="$bold"
                        color="$brand"
                      >
                        {`(${activeCount})`}
                      </SizableText>
                    )}
                  </XStack>
                }
              >
                <YStack gap="$3">
                  {section.fields.map((field) =>
                    renderReportFilterField({ field }),
                  )}
                </YStack>
              </FoldableSection>
            );
          })}
        </YStack>
      </ScrollView>
    </YStack>
  );

  const contentWithProvider = (
    <FormProvider
      control={control}
      register={register}
      handleSubmit={handleSubmit}
      unregister={unregister}
      getFieldState={getFieldState}
      setError={setError}
      clearErrors={clearErrors}
      setValue={setValue}
      trigger={trigger}
      formState={formState}
      resetField={resetField}
      reset={reset}
      getValues={getValues}
      watch={watch}
      setFocus={setFocus}
      subscribe={subscribe}
    >
      {content}
    </FormProvider>
  );

  if (variant === 'modal') {
    return (
      <Modal
        visible={open}
        animationType="slide"
        onRequestClose={onClose}
        presentationStyle="pageSheet"
        allowSwipeDismissal
        navigationBarTranslucent
        statusBarTranslucent
      >
        <YStack
          flex={1}
          justifyContent="center"
          alignItems="center"
          paddingVertical="$true"
        >
          {Platform.OS === 'ios' && <Grabber />}
          <YStack backgroundColor="$background" width="100%" height="100%">
            {contentWithProvider}
          </YStack>
        </YStack>
      </Modal>
    );
  }

  return (
    <YStack
      backgroundColor="$tertiary25"
      width={REPORT_FILTERS_PANEL_WIDTH}
      maxHeight="100%"
      borderWidth={1}
      borderColor="$tertiary50"
      display={open ? 'flex' : 'none'}
      pointerEvents={open ? 'auto' : 'none'}
    >
      {contentWithProvider}
    </YStack>
  );
};
